import { readShopifyConfig, storefrontFetch, type ShopifyConfig } from './shopify.client';
import { CommerceError, type CartLine, type CartLineInfo, type CartService } from './shopify.types';
import { readJson, removeKey, writeJson } from '../utils/storage';

const CART_KEY = 'chienheureux:virtual-shop:shopify-cart:v1';

/** Ligne enrichie de son identifiant Storefront, indispensable aux mutations. */
interface StoredLine extends CartLine {
  lineId: string;
}

interface StoredCart {
  cartId: string;
  checkoutUrl: string;
  lines: number;
  items: StoredLine[];
}

interface RawMerchandise {
  id?: string;
  title?: string;
  image?: { url?: string; altText?: string | null } | null;
  price?: { amount?: string; currencyCode?: string };
  product?: {
    title?: string;
    featuredImage?: { url?: string; altText?: string | null } | null;
  } | null;
}

interface RawLine {
  id?: string;
  quantity?: number;
  merchandise?: RawMerchandise | null;
}

interface RawCart {
  id?: string;
  checkoutUrl?: string;
  totalQuantity?: number;
  lines?: { nodes?: RawLine[] } | null;
}

interface CartPayload {
  cart?: RawCart | null;
  userErrors?: { message?: string }[];
}

const LINE_FIELDS = `lines(first: 50) {
    nodes {
      id
      quantity
      merchandise {
        ... on ProductVariant {
          id
          title
          image { url altText }
          price { amount currencyCode }
          product { title featuredImage { url altText } }
        }
      }
    }
  }`;

const CART_SELECTION = `id checkoutUrl totalQuantity ${LINE_FIELDS}`;
const CART_FIELDS = `cart { ${CART_SELECTION} } userErrors { field message }`;

function merchandiseTitle(merchandise: RawMerchandise): string {
  const product = merchandise.product?.title ?? '';
  // « Default Title » est le nom Shopify d'un produit sans déclinaison : inutile à l'écran.
  const variant =
    merchandise.title && merchandise.title !== 'Default Title' ? merchandise.title : '';
  if (product && variant) return `${product} (${variant})`;
  return product || variant || 'Article';
}

function toStoredLine(raw: RawLine): StoredLine | null {
  const merchandise = raw.merchandise;
  const amount = merchandise?.price?.amount;
  if (!raw.id || !merchandise?.id || amount === undefined) return null;

  const image = merchandise.image ?? merchandise.product?.featuredImage ?? null;
  const title = merchandiseTitle(merchandise);
  return {
    lineId: raw.id,
    variantId: merchandise.id,
    quantity: raw.quantity ?? 1,
    title,
    price: { amount, currencyCode: merchandise.price?.currencyCode ?? 'EUR' },
    imageUrl: image?.url ?? '',
    imageAlt: image?.altText ?? title,
  };
}

/** Panier Storefront réel. Le panier expiré est recréé au lieu de bloquer l'ajout. */
export class ShopifyCartService implements CartService {
  private cart: StoredCart | null;

  constructor(private readonly config: ShopifyConfig) {
    const stored = readJson<StoredCart>(CART_KEY);
    // Un panier écrit par une version précédente n'a pas de détail de lignes.
    this.cart = stored ? { ...stored, items: stored.items ?? [] } : null;
  }

  async createCart(): Promise<string> {
    if (this.cart) return this.cart.cartId;

    const data = await storefrontFetch<{ cartCreate?: CartPayload }>(
      this.config,
      `mutation CreateCart { cartCreate { ${CART_FIELDS} } }`,
    );
    const cart = data.cartCreate?.cart;
    if (!cart?.id || !cart.checkoutUrl) {
      throw new CommerceError('Le panier n’a pas pu être créé.');
    }

    this.cart = { cartId: cart.id, checkoutUrl: cart.checkoutUrl, lines: 0, items: [] };
    writeJson(CART_KEY, this.cart);
    return cart.id;
  }

  async addLine(variantId: string, quantity: number, info?: CartLineInfo): Promise<void> {
    if (!variantId) throw new CommerceError('Ce produit n’a pas de variante disponible.');
    if (quantity < 1) throw new CommerceError('Quantité invalide.');

    await this.createCart();
    const cartId = (this.cart as StoredCart).cartId;

    const mutation = `mutation AddLine($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) { ${CART_FIELDS} }
    }`;

    const data = await storefrontFetch<{ cartLinesAdd?: CartPayload }>(this.config, mutation, {
      cartId,
      // `sellingPlanId` n'est transmis que s'il existe : envoyer la clé à vide
      // ferait échouer la mutation côté Shopify.
      lines: [
        {
          merchandiseId: variantId,
          quantity,
          ...(info?.sellingPlanId ? { sellingPlanId: info.sellingPlanId } : {}),
        },
      ],
    });

    const result = data.cartLinesAdd;
    const error = result?.userErrors?.[0]?.message;
    if (error) throw new CommerceError(error);

    // Panier expiré côté Shopify : on repart d'un panier neuf, une seule fois.
    if (!result?.cart?.id) {
      removeKey(CART_KEY);
      this.cart = null;
      await this.createCart();
      const retry = await storefrontFetch<{ cartLinesAdd?: CartPayload }>(this.config, mutation, {
        cartId: (this.cart as unknown as StoredCart).cartId,
        lines: [{ merchandiseId: variantId, quantity }],
      });
      if (!retry.cartLinesAdd?.cart?.id) {
        throw new CommerceError('Le panier a expiré. Réessayez dans un instant.');
      }
      this.applyCart(retry.cartLinesAdd);
      return;
    }

    this.applyCart(result);
  }

  async updateLine(variantId: string, quantity: number): Promise<void> {
    if (quantity < 1) {
      await this.removeLine(variantId);
      return;
    }
    const lineId = await this.lineIdFor(variantId);
    const cart = this.cart;
    if (!lineId || !cart) throw new CommerceError('Cette ligne n’est plus dans le panier.');

    const data = await storefrontFetch<{ cartLinesUpdate?: CartPayload }>(
      this.config,
      `mutation UpdateLine($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) { ${CART_FIELDS} }
      }`,
      { cartId: cart.cartId, lines: [{ id: lineId, quantity }] },
    );

    const result = data.cartLinesUpdate;
    const error = result?.userErrors?.[0]?.message;
    if (error) throw new CommerceError(error);
    if (!result?.cart?.id) throw new CommerceError('La quantité n’a pas pu être modifiée.');
    this.applyCart(result);
  }

  async removeLine(variantId: string): Promise<void> {
    const lineId = await this.lineIdFor(variantId);
    const cart = this.cart;
    // Ligne déjà absente : le résultat souhaité est atteint, on ne fait pas d'erreur.
    if (!lineId || !cart) return;

    const data = await storefrontFetch<{ cartLinesRemove?: CartPayload }>(
      this.config,
      `mutation RemoveLine($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) { ${CART_FIELDS} }
      }`,
      { cartId: cart.cartId, lineIds: [lineId] },
    );

    const result = data.cartLinesRemove;
    const error = result?.userErrors?.[0]?.message;
    if (error) throw new CommerceError(error);
    if (!result?.cart?.id) throw new CommerceError('Le retrait n’a pas abouti.');
    this.applyCart(result);
  }

  async getLines(): Promise<CartLine[]> {
    if (!this.cart) return [];
    await this.refresh();
    return this.cart?.items ?? [];
  }

  /** Relit le panier chez Shopify : c'est lui qui fait foi sur les quantités. */
  private async refresh(): Promise<void> {
    const current = this.cart;
    if (!current) return;

    const data = await storefrontFetch<{ cart?: RawCart | null }>(
      this.config,
      `query CartLines($cartId: ID!) { cart(id: $cartId) { ${CART_SELECTION} } }`,
      { cartId: current.cartId },
    );

    // Panier expiré : mieux vaut l'oublier que d'afficher un contenu fantôme.
    if (!data.cart?.id) {
      removeKey(CART_KEY);
      this.cart = null;
      return;
    }
    this.applyCart({ cart: data.cart });
  }

  private async lineIdFor(variantId: string): Promise<string | null> {
    if (!variantId) return null;
    const find = (): string | null =>
      this.cart?.items.find((line) => line.variantId === variantId)?.lineId ?? null;

    const known = find();
    if (known) return known;
    await this.refresh();
    return find();
  }

  private applyCart(payload: CartPayload): void {
    const cart = payload.cart;
    if (!cart?.id || !cart.checkoutUrl) return;
    const nodes = cart.lines?.nodes;
    this.cart = {
      cartId: cart.id,
      checkoutUrl: cart.checkoutUrl,
      lines: cart.totalQuantity ?? 0,
      items: nodes
        ? nodes.map(toStoredLine).filter((line): line is StoredLine => line !== null)
        : (this.cart?.items ?? []),
    };
    writeJson(CART_KEY, this.cart);
  }

  async getCheckoutUrl(): Promise<string> {
    if (!this.cart || this.cart.lines === 0) {
      throw new CommerceError('Le panier est vide.');
    }
    return this.cart.checkoutUrl;
  }

  lineCount(): number {
    return this.cart?.lines ?? 0;
  }
}

export function createShopifyCart(): ShopifyCartService {
  const config = readShopifyConfig();
  if (!config) throw new CommerceError('Shopify n’est pas configuré.', false);
  return new ShopifyCartService(config);
}
